// src/components/CreditMemos.js — customer credits (credit memos): money we owe a customer back
// [marker: books-pro v1]
// <CreditMemos canEdit />                         Bookkeeping → Customer Credits
// <CreditMemoModal memoId? canEdit onClose onSaved />   create / edit, apply to invoices, refund by check, void
import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import DataGrid from './DataGrid';
import { AccountSelect } from './AccountSelect';
import { ClassSelect } from './ClassSelect';
import { PayerPicker, InvoiceFinder } from './ReceivePaymentModal';

const r2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;
const money = (n) => `${parseFloat(n || 0) < 0 ? '-' : ''}$${Math.abs(parseFloat(n || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDay = (d) => (d ? new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

export default function CreditMemos({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState('open');
  const [open, setOpen] = useState(null); // id | 'new'
  const load = useCallback(() => {
    setLoading(true);
    api.get(`/credit-memos?status=${show}`).then(r => setRows(r.data || [])).catch(() => toast.error('Could not load.')).finally(() => setLoading(false));
  }, [show]);
  useEffect(() => { load(); }, [load]);
  const columns = [
    { key: 'credit_date', label: 'Date', width: 105, type: 'date', get: r => r.credit_date + 'T00:00:00' },
    { key: 'credit_number', label: 'Credit #', width: 95 },
    { key: 'customer_name', label: 'Customer', width: 230, render: r => <strong>{r.customer_name}</strong> },
    { key: 'memo', label: 'Memo', width: 220 },
    { key: 'total', label: 'Amount', width: 105, type: 'money', align: 'right' },
    { key: 'applied', label: 'Applied', width: 100, type: 'money', align: 'right' },
    { key: 'refunded', label: 'Refunded', width: 100, type: 'money', align: 'right' },
    { key: 'remaining', label: 'Left to Use', width: 105, type: 'money', align: 'right' },
    { key: 'status', label: 'Status', width: 85, get: r => (r.status === 'void' ? 'Void' : r.remaining > 0.004 ? 'Open' : 'Used') },
  ];
  return (
    <div>
      <DataGrid gridKey="credit-memos" rows={rows} loading={loading} columns={columns} onRowClick={r => setOpen(r.id)}
        totals={['total', 'remaining']} defaultSort={{ key: 'credit_date', dir: 'desc' }}
        emptyText={show === 'open' ? 'No customer credits waiting to be used.' : 'No customer credits yet.'}
        toolbarLeft={(
          <select className="form-select" style={{ width: 170 }} value={show} onChange={e => setShow(e.target.value)}>
            <option value="open">Credits not used yet</option><option value="all">All credits</option>
          </select>
        )}
        toolbarRight={canEdit && <button className="btn btn-primary btn-sm" onClick={() => setOpen('new')}>+ New Customer Credit</button>} />
      <p className="form-hint" style={{ marginTop: 8 }}>
        A credit is money we owe a customer back — they were overcharged, paid twice, or are owed a discount. Use it on their next invoice,
        or refund it with a check. It reduces income (in the account and class you pick) and what the customer owes.
      </p>
      {open && <CreditMemoModal memoId={open === 'new' ? null : open} canEdit={canEdit} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />}
    </div>
  );
}

export function CreditMemoModal({ memoId, canEdit, onClose, onSaved }) {
  const isEdit = !!memoId;
  const [m, setM] = useState(null);
  const [form, setForm] = useState({ businessId: null, customerName: '', date: today(), memo: '' });
  const [lines, setLines] = useState([{ accountId: null, classId: null, description: '', amount: '' }]);
  const [invoices, setInvoices] = useState([]);     // open invoices to apply to
  const [apply, setApply] = useState({});           // invoiceId → amount
  const [refund, setRefund] = useState(null);       // { accountId, date, toPrint, number }
  const [banks, setBanks] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = useCallback(() => {
    if (!isEdit) return Promise.resolve();
    return api.get(`/credit-memos/${memoId}`).then(r => {
      const d = r.data; setM(d);
      setForm({ businessId: d.business_id, customerName: d.customer_name || '', date: d.credit_date, memo: d.memo || '' });
      setLines(d.lines.map(l => ({ accountId: l.account_id, classId: l.class_id, description: l.description || '', amount: String(r2(l.amount)) })));
      setApply({});
    }).catch(() => toast.error('Could not load the credit.'));
  }, [isEdit, memoId]);
  useEffect(() => { load(); api.get('/bookkeeping/register-accounts').then(r => setBanks((r.data || []).filter(a => a.account_type === 'bank'))).catch(() => {}); }, [load]);
  useEffect(() => {
    if (!form.businessId) { setInvoices(cur => cur.filter(i => apply[i.id])); return; }
    api.get(`/receivables/open-invoices?businessId=${form.businessId}`).then(r => setInvoices(r.data || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.businessId]);

  const total = r2(lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0));
  const left = isEdit && m ? r2(m.remaining) : total;
  const applying = r2(Object.values(apply).reduce((s, v) => s + (parseFloat(v) || 0), 0));
  const voided = m?.status === 'void';
  const ro = !canEdit || voided;
  const setLine = (i, k, v) => setLines(ls => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const autoApply = () => {
    let rest = left; const next = {};
    invoices.forEach(i => { const a = r2(Math.min(rest, r2(i.balance_due))); if (a > 0) { next[i.id] = String(a); rest = r2(rest - a); } });
    setApply(next);
  };
  const applyList = () => Object.entries(apply).filter(([, v]) => r2(v) > 0).map(([invoiceId, v]) => ({ invoiceId: parseInt(invoiceId, 10), amount: r2(v) }));

  const save = async () => {
    if (applying > left + 0.005) return toast.error(`You’re applying ${money(applying)} but only ${money(left)} is available.`);
    setBusy(true);
    try {
      const body = { ...form, lines: lines.map(l => ({ ...l, amount: r2(l.amount) })) };
      if (isEdit) {
        if (!ro) await api.patch(`/credit-memos/${memoId}`, body);
        if (applyList().length) await api.post(`/credit-memos/${memoId}/apply`, { apply: applyList(), date: today() });
      } else {
        await api.post('/credit-memos', { ...body, apply: applyList() });
      }
      toast.success(isEdit ? 'Saved.' : `Credit for ${form.customerName || 'customer'} saved${applying ? ` — ${money(applying)} applied` : ''}.`);
      onSaved && onSaved();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not save.'); }
    finally { setBusy(false); }
  };
  const unapply = async (a) => {
    if (!window.confirm(`Take this credit off invoice ${a.invoice_number}? The invoice will owe ${money(a.amount)} more.`)) return;
    try { await api.delete(`/credit-memos/applications/${a.id}`); toast.success('Removed.'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not remove it.'); }
  };
  const doRefund = async () => {
    if (!refund.accountId) return toast.error('Choose the bank account.');
    setBusy(true);
    try {
      await api.post(`/credit-memos/${memoId}/refund`, { ...refund, amount: r2(refund.amount) });
      toast.success(refund.toPrint ? 'Refund check recorded — it’s waiting under 🖨 Print Checks.' : 'Refund check recorded in the bank register.');
      setRefund(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Could not record the refund.'); }
    finally { setBusy(false); }
  };
  const voidIt = async () => {
    if (!window.confirm('Void this credit? It stays on file marked VOID and no longer counts.')) return;
    try { await api.post(`/credit-memos/${memoId}/void`); toast.success('Voided.'); onSaved && onSaved(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not void.'); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this credit? This cannot be undone.')) return;
    try { await api.delete(`/credit-memos/${memoId}`); toast.success('Deleted.'); onSaved && onSaved(); }
    catch (err) { toast.error(err.response?.data?.error || 'Could not delete.'); }
  };

  const used = m && (m.applications.length || m.refunds.length);
  return (
    <div className="modal-overlay" onClick={onClose} style={{ zIndex: 1450 }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ maxWidth: 920 }}>
        <div className="modal-header">
          <span className="modal-title">{isEdit ? `Customer Credit ${m?.credit_number || ''}${voided ? ' (VOID)' : ''}` : 'New Customer Credit'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
          {isEdit && !m ? <div className="spinner" /> : (
            <>
              {voided && <div style={{ fontSize: '0.85rem', background: 'var(--gray-100)', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}><strong>VOID</strong> — kept for the record only.</div>}
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
                <div className="form-group"><label className="form-label">Customer *</label>
                  <PayerPicker value={form.customerName} disabled={ro || !!used}
                    onChange={v => setForm(f => ({ ...f, customerName: v, businessId: null }))}
                    onPickBusiness={b => setForm(f => ({ ...f, customerName: b.business_name, businessId: b.id }))} />
                  {!form.businessId && form.customerName && <p className="form-hint">Not linked to a member business — pick one from the list to see their invoices.</p>}
                </div>
                <div className="form-group"><label className="form-label">Date *</label>
                  <input className="form-input" type="date" value={form.date} disabled={ro} onChange={e => set('date', e.target.value)} /></div>
              </div>
              <div className="form-group"><label className="form-label">Memo / reason</label>
                <input className="form-input" value={form.memo} disabled={ro} onChange={e => set('memo', e.target.value)} placeholder="e.g. Charged twice for the luncheon" /></div>

              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', margin: '4px 0' }}>What it’s for (usually the income account the sale went to)</div>
              {lines.map((l, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.4fr 1.6fr 1fr auto', gap: 6, marginBottom: 6 }}>
                  <AccountSelect value={l.accountId} disabled={ro} types={['income', 'other_income', 'expense', 'other_expense']} onChange={v => setLine(i, 'accountId', v)} placeholder="— Income account —" />
                  <ClassSelect value={l.classId} disabled={ro} onChange={v => setLine(i, 'classId', v)} />
                  <input className="form-input" placeholder="Description" value={l.description} disabled={ro} onChange={e => setLine(i, 'description', e.target.value)} />
                  <input className="form-input" type="number" step="0.01" placeholder="Amount" style={{ textAlign: 'right' }} value={l.amount} disabled={ro} onChange={e => setLine(i, 'amount', e.target.value)} />
                  <button className="btn btn-ghost btn-sm" disabled={ro || lines.length === 1} onClick={() => setLines(ls => ls.filter((_, j) => j !== i))}>✕</button>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {!ro ? <button className="btn btn-secondary btn-sm" onClick={() => setLines(ls => [...ls, { accountId: null, classId: null, description: '', amount: '' }])}>+ Add line</button> : <span />}
                <span>Credit amount: <strong>{money(total)}</strong>{isEdit && m ? <> · left to use <strong>{money(m.remaining)}</strong></> : null}</span>
              </div>

              {m && used ? (
                <div style={{ marginTop: 12, fontSize: '0.85rem' }}>
                  {m.applications.map(a => (
                    <div key={a.id}>✓ Used {money(a.amount)} on invoice <strong>{a.invoice_number}</strong> ({fmtDay(a.applied_date)})
                      {canEdit && !voided && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)', padding: '0 6px' }} onClick={() => unapply(a)}>remove</button>}</div>
                  ))}
                  {m.refunds.map(f => (
                    <div key={f.id}>↩ Refunded {money(f.amount)} by check {f.number || ''} from {f.account_name} ({fmtDay(f.date)}){f.voided_at ? ' — VOID' : ''}
                      <span className="form-hint" style={{ margin: '0 0 0 6px' }}>(void or delete it from the bank register)</span></div>
                  ))}
                </div>
              ) : null}

              {!voided && canEdit && left > 0.004 && (
                <div style={{ marginTop: 14, border: '1px solid var(--gray-200)', borderRadius: 6, padding: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
                    <strong style={{ fontSize: '0.85rem' }}>Use it on open invoices</strong>
                    <span style={{ display: 'flex', gap: 6 }}>
                      <InvoiceFinder onAdd={list => setInvoices(cur => [...cur, ...list.filter(x => !cur.some(c => c.id === x.id))])} />
                      {invoices.length > 0 && <button className="btn btn-secondary btn-sm" onClick={autoApply}>Fill in automatically</button>}
                    </span>
                  </div>
                  {invoices.length === 0 ? <p className="form-hint" style={{ margin: 0 }}>{form.businessId ? 'This customer has no open invoices. Find another invoice, or refund it below.' : 'Pick the customer (or find an invoice) to use the credit on it.'}</p> : (
                    <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                      <tbody>
                        {invoices.map(i => (
                          <tr key={i.id} style={{ borderTop: '1px solid var(--gray-100)' }}>
                            <td style={{ padding: 4 }}>{i.invoice_number}</td><td style={{ padding: 4 }}>{fmtDay(i.issue_date)}</td>
                            <td style={{ padding: 4 }}>{i.business_name || i.recipient_name}</td><td style={{ padding: 4, color: 'var(--gray-500)' }}>{i.items}</td>
                            <td style={{ padding: 4, textAlign: 'right' }}>{money(i.balance_due)} due</td>
                            <td style={{ padding: 4, width: 120 }}><input className="form-input" type="number" step="0.01" style={{ height: 28, textAlign: 'right' }} value={apply[i.id] || ''} placeholder="0.00"
                              onChange={e => setApply(a => ({ ...a, [i.id]: e.target.value }))} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {isEdit && !refund && <div style={{ marginTop: 8 }}><button className="btn btn-ghost btn-sm" onClick={() => setRefund({ accountId: banks[0]?.id || null, date: today(), toPrint: true, number: '', amount: String(left) })}>↩ Refund it with a check instead…</button></div>}
                  {refund && (
                    <div style={{ marginTop: 8, display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                      <div><label className="form-label">From bank account</label>
                        <select className="form-select" value={refund.accountId || ''} onChange={e => setRefund(r => ({ ...r, accountId: parseInt(e.target.value, 10) }))}>
                          {banks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}</select></div>
                      <div><label className="form-label">Date</label><input className="form-input" type="date" value={refund.date} onChange={e => setRefund(r => ({ ...r, date: e.target.value }))} /></div>
                      <div><label className="form-label">Amount</label><input className="form-input" type="number" step="0.01" value={refund.amount} onChange={e => setRefund(r => ({ ...r, amount: e.target.value }))} /></div>
                      <div><label className="form-label">Check #</label>
                        <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: '0.78rem' }}><input type="checkbox" checked={refund.toPrint} onChange={e => setRefund(r => ({ ...r, toPrint: e.target.checked }))} /> Print it</label>
                        {!refund.toPrint && <input className="form-input" value={refund.number} onChange={e => setRefund(r => ({ ...r, number: e.target.value }))} />}</div>
                      <span style={{ display: 'flex', gap: 4 }}><button className="btn btn-primary btn-sm" onClick={doRefund} disabled={busy}>Record refund</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setRefund(null)}>Cancel</button></span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <span style={{ display: 'flex', gap: 4 }}>
            {isEdit && canEdit && !voided && !used && <button className="btn btn-ghost" style={{ color: 'var(--danger)' }} onClick={remove}>Delete</button>}
            {isEdit && canEdit && !voided && !used && <button className="btn btn-ghost" onClick={voidIt}>Void</button>}
          </span>
          <span style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>{canEdit ? 'Cancel' : 'Close'}</button>
            {canEdit && !voided && <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : applying ? `Save & apply ${money(applying)}` : 'Save'}</button>}
          </span>
        </div>
      </div>
    </div>
  );
}
